"use client";

import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { Form } from "@/components/forms/Form";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StarRating } from "@/components/ui/star-rating";
import { useFeedbackForm } from "@/hooks/feedback/useFeedbackForm";
import {
  type FeedbackFormValues,
  makeFeedbackFormSchema,
} from "@/lib/validations/features/feedback";
import {
  FeedbackDepartmentEnum,
  FeedbackTypeEnum,
  RELATED_PAGE_OPTIONS,
} from "@/types/feedback";
import { CharacterCounter } from "./CharacterCounter";

export const FeedbackForm: React.FC = () => {
  const vt = useTranslations("validation");
  const schema = useMemo(() => makeFeedbackFormSchema(vt), [vt]);

  const defaultValues: Partial<FeedbackFormValues> = {
    rating: 0,
    type: FeedbackTypeEnum.SUGGESTION,
    department: FeedbackDepartmentEnum.GENERAL,
    message: "",
  };

  return (
    <Form
      schema={schema}
      onSubmit={() => {}}
      defaultValues={defaultValues}
      mode="onSubmit"
      reValidateMode="onChange"
      className="space-y-4"
    >
      {(form) => <FeedbackFormInner form={form} />}
    </Form>
  );
};

const FeedbackFormInner: React.FC<{
  form: import("react-hook-form").UseFormReturn<FeedbackFormValues>;
}> = ({ form }) => {
  const t = useTranslations("feedback");
  const mutation = useFeedbackForm(() => {
    form.reset();
  });

  const handleSubmit = (data: FeedbackFormValues) => {
    mutation.mutate({
      body: {
        rating: data.rating,
        type: data.type,
        department: data.department,
        related_page: data.related_page || undefined,
        message: data.message,
      },
    });
  };

  // Re-attach the form submit handler
  const onSubmit = form.handleSubmit(handleSubmit);

  return (
    <div className="bg-bg-secondary rounded-md py-5 px-4 space-y-4 max-w-xl mx-auto">
      {/* Star Rating */}
      <Field orientation="vertical">
        <FieldLabel>{t("form.rating")}</FieldLabel>
        <FieldContent>
          <Controller
            name="rating"
            control={form.control}
            render={({ field }) => (
              <div className="flex justify-center">
                <StarRating
                  value={field.value}
                  onChange={field.onChange}
                  size="lg"
                />
              </div>
            )}
          />
        </FieldContent>
        <FieldError
          errors={
            form.formState.errors.rating
              ? [form.formState.errors.rating]
              : undefined
          }
        />
      </Field>

      {/* Type & Department dropdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field orientation="vertical">
          <FieldLabel>{t("form.type")}</FieldLabel>
          <FieldContent>
            <Controller
              name="type"
              control={form.control}
              render={({ field }) => (
                <Select
                  dir="rtl"
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger
                    className={`w-full ${form.formState.errors.type ? "border-semantic-error" : ""}`}
                  >
                    <SelectValue placeholder={t("form.type")} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(FeedbackTypeEnum).map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`type.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FieldContent>
          <FieldError
            errors={
              form.formState.errors.type
                ? [form.formState.errors.type]
                : undefined
            }
          />
        </Field>

        <Field orientation="vertical">
          <FieldLabel>{t("form.department")}</FieldLabel>
          <FieldContent>
            <Controller
              name="department"
              control={form.control}
              render={({ field }) => (
                <Select
                  dir="rtl"
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger
                    className={`w-full ${form.formState.errors.department ? "border-semantic-error" : ""}`}
                  >
                    <SelectValue placeholder={t("form.department")} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(FeedbackDepartmentEnum).map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {t(`department.${dept}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FieldContent>
          <FieldError
            errors={
              form.formState.errors.department
                ? [form.formState.errors.department]
                : undefined
            }
          />
        </Field>
      </div>

      {/* Related Page (optional) */}
      <Field orientation="vertical">
        <FieldLabel>{t("form.relatedPage")}</FieldLabel>
        <FieldContent>
          <Controller
            name="related_page"
            control={form.control}
            render={({ field }) => (
              <Select
                dir="rtl"
                value={field.value ?? ""}
                onValueChange={(val) => field.onChange(val || undefined)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("form.relatedPage")} />
                </SelectTrigger>
                <SelectContent>
                  {RELATED_PAGE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(`relatedPages.${option.value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FieldContent>
      </Field>

      {/* Message Textarea */}
      <Field orientation="vertical">
        <FieldLabel>{t("form.message")}</FieldLabel>
        <FieldContent>
          <textarea
            {...form.register("message")}
            placeholder={t("form.messagePlaceholder")}
            className={`w-full h-32 resize-none rounded-xs border bg-transparent px-3 py-2 text-sm placeholder:text-text-secondary shadow-xs transition-colors focus-visible:outline-none focus-visible:border-stroke-primary ${
              form.formState.errors.message
                ? "border-semantic-error"
                : "border-stroke-light"
            }`}
          />
        </FieldContent>
        <CharacterCounter
          current={form.watch("message")?.length ?? 0}
          max={5000}
        />
        <FieldError
          errors={
            form.formState.errors.message
              ? [form.formState.errors.message]
              : undefined
          }
        />
      </Field>

      {/* Submit Button */}
      <Button
        type="button"
        variant="solid"
        colors="primary"
        size="lg"
        className="w-full"
        loading={mutation.isPending}
        loadingText={t("form.submitting")}
        onClick={onSubmit}
      >
        {t("form.submit")}
      </Button>
    </div>
  );
};
